import React from "react";
import {
  readGridState,
  writeGridState,
  clearGridState,
  mergeGridControlledModels,
  PartialGridControlledModels,
} from "../utils/gridStatePersistence";

export function usePersistedGridState(
  apiRef: React.MutableRefObject<any>,
  storageKey: string,
  options?: {
    controlledModels?: PartialGridControlledModels;
  }
) {
  const saveTimeoutRef = React.useRef<number | null>(null);
  const controlledModelsRef = React.useRef(options?.controlledModels);
  controlledModelsRef.current = options?.controlledModels;

  const initialState = React.useMemo(() => {
    return readGridState(storageKey);
  }, [storageKey]);

  const saveState = React.useCallback(() => {
    const api = apiRef.current;

    if (!api || typeof api.exportState !== "function") {
      return;
    }

    try {
      const state = mergeGridControlledModels(
        api.exportState(),
        controlledModelsRef.current
      );

      writeGridState(storageKey, state);
    } catch (error) {
      console.warn("[DataGrid] Nie udało się zapisać stanu tabeli", error);
    }
  }, [apiRef, storageKey]);

  const scheduleSave = React.useCallback(() => {
    if (typeof window === "undefined") return;

    if (saveTimeoutRef.current !== null) {
      window.clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = window.setTimeout(() => {
      saveTimeoutRef.current = null;
      saveState();
    }, 150);
  }, [saveState]);

  React.useLayoutEffect(() => {
    if (typeof window === "undefined") return;

    const handleBeforeUnload = () => {
      saveState();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);

      if (saveTimeoutRef.current !== null) {
        window.clearTimeout(saveTimeoutRef.current);
      }

      saveState();
    };
  }, [saveState]);

  const resetState = React.useCallback(() => {
    clearGridState(storageKey);
  }, [storageKey]);

  return {
    initialState,
    saveState,
    scheduleSave,
    resetState,
  };
}
